import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  ArrowLeft, ArrowRight, Loader2, Download, RefreshCw, Zap, Target,
  Check, AlertCircle, Calendar, Star, TrendingUp, Shield
} from 'lucide-react';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = {
  'Emerging': { bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-300', bar: 'from-amber-400 to-amber-500' },
  'Rising': { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-300', bar: 'from-purple-400 to-purple-500' },
  'Established': { bg: 'bg-pink-500/20', border: 'border-pink-500/30', text: 'text-pink-300', bar: 'from-pink-400 to-pink-500' },
  'Elite': { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-300', bar: 'from-emerald-400 to-emerald-500' }
};

export default function ProfileAnalyzer() {
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const pdfRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => { loadExisting(); }, []);

  const loadExisting = async () => {
    try {
      const res = await axios.get(`${API}/profile-analyzer/${user.user_id}`);
      if (res.data.status !== 'not_found') setAnalysis(res.data.analysis);
    } catch (e) { /* none */ }
    setInitialLoading(false);
  };

  const generateAnalysis = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/profile-analyzer/generate`, { user_id: user.user_id });
      if (res.data.status === 'success') {
        setAnalysis(res.data.analysis);
        toast.success('Profile analysis generated!');
      } else {
        toast.error('Failed to generate analysis');
      }
    } catch (e) {
      toast.error('Something went wrong');
    } finally { setLoading(false); }
  };

  const downloadPDF = async () => {
    if (!pdfRef.current) return;
    toast.info('Generating PDF...');
    try {
      const canvas = await html2canvas(pdfRef.current, { scale: 2, backgroundColor: '#f9fafb', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = pdfHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
      
      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }
      
      pdf.save('Pivot-To-Power-Profile-Analysis.pdf');
      toast.success('PDF downloaded!');
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  if (initialLoading) {
    return <AppShell><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div></AppShell>;
  }

  const lc = analysis ? (LEVEL_COLORS[analysis.readiness_level] || LEVEL_COLORS['Emerging']) : null;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="analyzer-title">Profile Analyzer</h1>
            <p className="text-[#A39BB0] font-inter text-sm">Your comprehensive power assessment & personalized 90-day roadmap</p>
          </div>
          {analysis && (
            <button onClick={downloadPDF} className="flex items-center space-x-2 bg-white/10 hover:bg-white/15 text-white font-inter text-sm px-4 py-2 rounded-full transition-all border border-white/10" data-testid="download-pdf-button">
              <Download className="w-4 h-4" /><span>Download PDF</span>
            </button>
          )}
        </div>

        {!analysis ? (
          <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-10 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-purple-500/20">
              <TrendingUp className="w-8 h-8 text-purple-300" />
            </div>
            <h2 className="text-2xl font-cormorant font-bold text-white mb-2">Generate Your Power Profile</h2>
            <p className="text-[#A39BB0] font-inter text-sm max-w-md mx-auto mb-6 leading-relaxed">
              Our AI will analyze your archetype, onboarding data, and Pivot Journey answers to create a comprehensive assessment with a personalized 30-60-90 day roadmap.
            </p>
            <button onClick={generateAnalysis} disabled={loading}
              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-8 py-3 rounded-full shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all disabled:opacity-50"
              data-testid="generate-analysis-button">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Analyzing Your Profile...</> : <>Generate Analysis <Zap className="w-4 h-4 inline ml-1" /></>}
            </button>
            <p className="text-[#A39BB0]/60 text-xs font-inter mt-3">Takes about 15 seconds</p>
          </div>
        ) : (
          <div ref={pdfRef} className="space-y-6 pb-12 bg-white rounded-2xl p-6" style={{ color: '#111827' }}>
            {/* Power Score */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid="power-score-card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-cormorant font-bold text-gray-900">Power Score</h2>
                  <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-inter font-semibold ${lc.bg} ${lc.text} ${lc.border} border mt-1`}>
                    <Star className="w-3 h-3" /><span>{analysis.readiness_level}</span>
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-cormorant font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{analysis.power_score}</div>
                  <div className="text-gray-400 text-xs font-inter">out of 100</div>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3">
                <div className={`bg-gradient-to-r ${lc.bar} h-3 rounded-full transition-all`} style={{ width: `${analysis.power_score}%` }} />
              </div>
            </div>

            {/* Cultural Premium */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-8" data-testid="cultural-premium-card">
              <h2 className="text-xl font-cormorant font-bold text-gray-900 mb-3 flex items-center space-x-2">
                <Shield className="w-5 h-5 text-purple-600" /><span>Your Cultural Premium</span>
              </h2>
              <p className="text-gray-700 font-inter text-sm leading-relaxed">{analysis.cultural_premium_summary}</p>
            </div>

            {/* Strengths & Gaps */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-6" data-testid="strengths-card">
                <h3 className="text-lg font-cormorant font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Check className="w-5 h-5 text-emerald-500" /><span>Strengths</span>
                </h3>
                <ul className="space-y-3">
                  {analysis.strengths_analysis.map((s, i) => (
                    <li key={i} className="flex items-start space-x-2 text-gray-700 font-inter text-sm">
                      <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-emerald-600" />
                      </div>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-6" data-testid="gaps-card">
                <h3 className="text-lg font-cormorant font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" /><span>Growth Areas</span>
                </h3>
                <ul className="space-y-3">
                  {analysis.gap_analysis.map((g, i) => (
                    <li key={i} className="flex items-start space-x-2 text-gray-700 font-inter text-sm">
                      <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Target className="w-3 h-3 text-amber-600" />
                      </div>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 30-60-90 Roadmap */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid="roadmap-card">
              <h2 className="text-2xl font-cormorant font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <Calendar className="w-6 h-6 text-purple-600" /><span>Your 30-60-90 Day Roadmap</span>
              </h2>
              <div className="space-y-6">
                {[
                  { key: 'days_1_30', label: 'Days 1–30', color: 'purple', icon: '01' },
                  { key: 'days_31_60', label: 'Days 31–60', color: 'pink', icon: '02' },
                  { key: 'days_61_90', label: 'Days 61–90', color: 'emerald', icon: '03' },
                ].map(phase => {
                  const data = analysis.roadmap[phase.key];
                  if (!data) return null;
                  return (
                    <div key={phase.key} className={`border border-${phase.color}-100 rounded-xl overflow-hidden`}>
                      <div className={`bg-${phase.color}-50 px-6 py-3 flex items-center space-x-3`}>
                        <div className={`w-8 h-8 bg-${phase.color}-600 rounded-lg flex items-center justify-center`}>
                          <span className="text-white text-xs font-inter font-bold">{phase.icon}</span>
                        </div>
                        <div>
                          <div className={`text-${phase.color}-700 text-xs font-inter font-bold uppercase tracking-wider`}>{phase.label}</div>
                          <div className="text-gray-900 font-cormorant font-bold">{data.theme}</div>
                        </div>
                      </div>
                      <div className="px-6 py-4">
                        <ul className="space-y-2">
                          {data.actions.map((a, i) => (
                            <li key={i} className="flex items-start space-x-2 text-gray-700 font-inter text-sm">
                              <ArrowRight className={`w-4 h-4 text-${phase.color}-400 flex-shrink-0 mt-0.5`} />
                              <span>{a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Focus */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-8 text-center">
              <h3 className="text-white font-cormorant font-bold text-2xl mb-2">Recommended Focus</h3>
              <p className="text-white/90 font-inter leading-relaxed">{analysis.recommended_focus}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center space-x-3">
              <button onClick={generateAnalysis} disabled={loading}
                className="flex items-center space-x-2 bg-white border border-gray-200 text-gray-700 font-inter text-sm px-5 py-2.5 rounded-full hover:border-purple-300 transition-all disabled:opacity-50"
                data-testid="regenerate-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span>Regenerate</span>
              </button>
              <button onClick={() => navigate('/idea-lab')}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold text-sm px-5 py-2.5 rounded-full hover:shadow-lg hover:shadow-purple-200 transition-all"
                data-testid="go-to-idea-lab">
                <span>Try the Idea Lab</span><ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
